import React, { useState } from 'react';

const titleCaseValue = (value = '') => String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const isValidTimelineSourceDate = (value = '') => {
    const text = String(value || '').trim();
    let year = 0;
    let month = 0;
    let day = 0;
    let match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
    if (match) {
        [, year, month, day] = match.map(Number);
    } else {
        match = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
        if (match) {
            month = Number(match[1]);
            day = Number(match[2]);
            year = Number(match[3]);
        } else {
            match = text.match(/^(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+(\d{1,2}),?\s+(\d{4})$/i);
            if (!match) return false;
            const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            month = monthNames.indexOf(match[1].slice(0, 3).toLowerCase()) + 1;
            day = Number(match[2]);
            year = Number(match[3]);
        }
    }
    if (year < 1 || month < 1 || month > 12 || day < 1) return false;
    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const monthDays = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= monthDays[month - 1];
};

export const isCitationReadyDocumentTimelineEvent = (event = {}) => {
    const date = String(event?.date || '').trim();
    const description = String(event?.description || '').trim();
    const sources = Array.isArray(event?.sources) ? event.sources : [];
    return isValidTimelineSourceDate(date) && description !== '' && sources.some(source => (
        String(source?.source_type || '') === 'document_text'
        && Number(source?.citation_number || 0) > 0
        && String(source?.file_name || '').trim() !== ''
        && Number(source?.page_start || source?.page || 0) > 0
        && String(source?.quote || '').trim() !== ''
    ));
};

export const normalizeSourcedTimeline = (timelineResult = null) => {
    if (!timelineResult || typeof timelineResult !== 'object') return null;
    const rawEvents = Array.isArray(timelineResult.timeline) ? timelineResult.timeline.filter(Boolean) : [];
    const coverage = timelineResult.coverage && typeof timelineResult.coverage === 'object' ? timelineResult.coverage : {};
    const warnings = Array.isArray(timelineResult.warnings) ? timelineResult.warnings.filter(Boolean) : [];
    const requestedDocumentTypes = Array.isArray(timelineResult.requested_document_types) ? timelineResult.requested_document_types.filter(Boolean) : [];
    const documentOnlyScope = requestedDocumentTypes.length > 0 || String(timelineResult.scope || '') === 'selected_documents';
    const events = documentOnlyScope
        ? rawEvents.filter(isCitationReadyDocumentTimelineEvent)
        : rawEvents;
    if (events.length < rawEvents.length) {
        warnings.push({
            code: 'INVALID_EVENT_EVIDENCE_SUPPRESSED',
            message: `${rawEvents.length - events.length} document chronology event(s) lacked a date, description, citation number, source document, positive physical page, or supporting excerpt and were not displayed.`,
        });
    }
    if (events.length === 0 && Object.keys(coverage).length === 0 && warnings.length === 0) return null;
    return {
        caseId: Number(timelineResult.case_id || 0) || null,
        caseName: String(timelineResult.case_name || 'Case timeline').trim() || 'Case timeline',
        eventCount: events.length,
        requestedDocumentTypes,
        coverage,
        stats: timelineResult.stats && typeof timelineResult.stats === 'object' ? timelineResult.stats : {},
        warnings,
        events,
    };
};

export const getSourcedTimelineFromAnswer = (answer = null) => normalizeSourcedTimeline(answer?.timeline_result || null);

export const hasSourcedTimelineReview = (timeline = null) => !!timeline && (
    (Array.isArray(timeline?.events) && timeline.events.length > 0)
    || (timeline?.coverage && Object.keys(timeline.coverage).length > 0)
    || (Array.isArray(timeline?.warnings) && timeline.warnings.length > 0)
);

const getEventSourceTypes = (event = {}) => (
    Array.isArray(event?.sources) ? event.sources : []
).map(source => String(source?.source_type || source?.type || '').trim()).filter(Boolean);

const getTimelineSourceGroup = (event = {}) => {
    const eventType = String(event?.event_type || '').trim();
    const sourceTypes = getEventSourceTypes(event);
    if (event?.document_text_event || eventType === 'document_text' || sourceTypes.includes('document_text')) {
        return 'document_text';
    }
    if (event?.metadata_date_only || eventType === 'document_metadata' || sourceTypes.includes('document')) {
        return 'document_metadata';
    }
    if (sourceTypes.includes('feed_update') || String(event?.feed_type || '').trim()) {
        return 'feed_update';
    }
    if (sourceTypes.includes('custom_field')) {
        return 'field_update';
    }
    if (sourceTypes.includes('case_summary') || sourceTypes.includes('case_fact')) {
        return 'case_fact';
    }
    return 'other';
};

const TIMELINE_SOURCE_GROUP_LABELS = {
    case_fact: 'Case facts',
    field_update: 'Field updates',
    feed_update: 'Feed updates',
    document_metadata: 'Document metadata',
    document_text: 'Document text',
    other: 'Other sources',
};

const TIMELINE_SOURCE_GROUP_ORDER = ['case_fact', 'field_update', 'feed_update', 'document_metadata', 'document_text', 'other'];

const getSourcedTimelineFilterOptions = (events = []) => {
    const groups = new Set(events.map(getTimelineSourceGroup));
    return TIMELINE_SOURCE_GROUP_ORDER
        .filter(group => groups.has(group))
        .map(group => ({ value: group, label: TIMELINE_SOURCE_GROUP_LABELS[group] || titleCaseValue(group) }));
};

const eventMatchesTimelineFilter = (event = {}, selectedFilter = 'all') => (
    selectedFilter === 'all' || getTimelineSourceGroup(event) === selectedFilter
);

const timelineSourceInteger = (value, minimum = 1) => {
    if (typeof value !== 'number' && (typeof value !== 'string' || !/^\d+$/.test(value))) return null;
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= minimum ? number : null;
};

const timelineSourcePages = (source) => {
    for (const key of ['page', 'page_start', 'page_end']) {
        if (source[key] != null && timelineSourceInteger(source[key]) === null) return null;
    }
    const start = timelineSourceInteger(source.page_start ?? source.page);
    const end = timelineSourceInteger(source.page_end ?? source.page ?? start);
    return start !== null && end !== null && end >= start ? [start, end] : null;
};

const sameTimelineDocumentTarget = (source, citation) => {
    if (String(citation?.source_type || '') !== 'document_text'
        || timelineSourceInteger(citation.file_id) !== timelineSourceInteger(source.file_id)) return false;
    const sourcePages = timelineSourcePages(source);
    const citationPages = timelineSourcePages(citation);
    if (!sourcePages || !citationPages || sourcePages.some((page, index) => page !== citationPages[index])) return false;
    for (const key of ['case_id', 'registry_id', 'chunk_index']) {
        // Missing identity is not permission to borrow a revision/chunk from
        // the card; the viewer uses those fields to choose the actual target.
        if ((source[key] != null) !== (citation[key] != null)) return false;
        if (source[key] != null) {
            const minimum = key === 'chunk_index' ? 0 : 1;
            const value = timelineSourceInteger(source[key], minimum);
            if (value === null || value !== timelineSourceInteger(citation[key], minimum)) return false;
        }
    }
    return true;
};

export const getSourcedTimelineCitation = (source = {}, citations = []) => {
    if (String(source?.source_type || '') === 'document_text') {
        const number = timelineSourceInteger(source.citation_number ?? source.number ?? source.source_number);
        const fileId = timelineSourceInteger(source.file_id);
        const pages = timelineSourcePages(source);
        if (number === null || fileId === null || pages === null) return null;
        // A citation number or a file alone is not a page/evidence identity.
        // Never borrow another event's page, excerpt or viewer target. An
        // unambiguous matching card may enrich the source's own target only.
        const matches = citations.filter(citation => (
            timelineSourceInteger(citation?.original_number ?? citation?.number ?? citation?.citation_number) === number
            && sameTimelineDocumentTarget(source, citation)
        ));
        const match = matches.length === 1 ? matches[0] : {};
        return {
            ...match,
            ...source,
            number,
            original_number: number,
            file_id: fileId,
            page_start: pages[0],
            page_end: pages[1],
            source_type: 'document_text',
            chunk_source: source.chunk_source || match.chunk_source || 'sourced_link_timeline',
            excerpt: source.quote || source.excerpt || '',
            label: source.label || source.file_name || `Source ${number}`,
        };
    }
    const citationNumber = Number(source?.citation_number || source?.number || source?.source_number || 0);
    if (citationNumber > 0) {
        const match = citations.find(citation => Number(citation?.number || citation?.citation_number || citation?.original_number || 0) === citationNumber);
        if (match) return {
            ...source,
            ...match,
            number: match.number || citationNumber,
            original_number: match.original_number || citationNumber,
            case_id: match.case_id || source?.case_id || null,
            case_name: match.case_name || source?.case_name || '',
            file_id: match.file_id || source?.file_id || null,
            url: match.url || source?.url || '',
            label: match.label || source?.label || `Source ${citationNumber}`,
        };
    }
    const fileId = Number(source?.file_id || 0);
    if (fileId > 0) {
        const match = citations.find(citation => Number(citation?.file_id || 0) === fileId);
        if (match) return {
            ...source,
            ...match,
            file_id: match.file_id || fileId,
            case_id: match.case_id || source?.case_id || null,
            case_name: match.case_name || source?.case_name || '',
            url: match.url || source?.url || '',
        };
    }
    const caseId = Number(source?.case_id || 0);
    const hasTimelineSourceTarget = fileId > 0 || caseId > 0 || source?.url;
    if (!hasTimelineSourceTarget) return null;
    return {
        ...source,
        number: citationNumber || source?.number || null,
        original_number: citationNumber || source?.original_number || null,
        source_type: source?.source_type || source?.type || 'sourced_link_timeline',
        chunk_source: source?.chunk_source || 'sourced_link_timeline',
        file_id: fileId || null,
        case_id: caseId || null,
        file_name: source?.file_name || source?.label || 'Timeline source',
        label: source?.label || (citationNumber ? `Source ${citationNumber}` : 'Timeline source'),
    };
};

const SourcedTimelineEventCard = ({ event, citations = [], onCitationOpen, full = false, expandText = true }) => {
    const [expanded, setExpanded] = useState(false);
    const [sourcesExpanded, setSourcesExpanded] = useState(false);
    const sources = Array.isArray(event?.sources) ? event.sources : [];
    const sourcePreviewLimit = full ? 12 : 4;
    const hiddenSourceCount = Math.max(0, sources.length - sourcePreviewLimit);
    const evidenceSource = sources.find(source => (
        Number(source?.citation_number || 0) > 0
        && String(source?.file_name || '').trim() !== ''
        && Number(source?.page_start || source?.page || 0) > 0
        && String(source?.quote || '').trim() !== ''
    )) || sources[0] || {};
    const clusteredEvents = Array.isArray(event?.clustered_events) ? event.clustered_events : [];
    const description = String(event?.description || event?.title || '').trim();
    const descriptionRepeatsTitle = description === String(event?.title || '').trim();
    const supportingExcerpt = String(evidenceSource?.quote || '').trim();
    const showCompleteText = !expandText || full || expanded;
    const displayExcerpt = showCompleteText || supportingExcerpt.length <= 260 ? supportingExcerpt : `${supportingExcerpt.slice(0, 260).trimEnd()}…`;
    const visibleClusteredEvents = expandText ? clusteredEvents.slice(0, full ? 20 : 6) : clusteredEvents;
    const clusterCount = Number(event?.cluster_count || 1);
    const sourceGroup = getTimelineSourceGroup(event);
    const sourceGroupLabel = TIMELINE_SOURCE_GROUP_LABELS[sourceGroup] || titleCaseValue(sourceGroup);
    const classifiedDocumentType = String(event?.document_event_type || '').trim();
    const displayEventType = classifiedDocumentType || String(event?.event_type || 'other').trim() || 'other';

    return (
        <article className={`library-rag-sourced-timeline-event ${String(event?.importance || 'low')}`}>
            <div className="library-rag-sourced-timeline-date">{String(event?.date || 'unknown')}</div>
            <div className="library-rag-sourced-timeline-card-body">
                <div className="library-rag-sourced-timeline-event-header">
                    <h4>{String(event?.title || 'Timeline event')}</h4>
                    <div className="library-rag-sourced-timeline-badges">
                        <span>{sourceGroupLabel}</span>
                        {!['document_metadata'].includes(sourceGroup) ? <span>{titleCaseValue(displayEventType)}</span> : null}
                        <span>{titleCaseValue(event?.importance || 'low')}</span>
                        {clusterCount > 1 ? <span>{clusterCount} grouped</span> : null}
                    </div>
                </div>
                {description && !descriptionRepeatsTitle ? (
                    <p><span className="library-rag-sourced-timeline-excerpt-label">Description:</span> {description}</p>
                ) : null}
                {displayExcerpt ? (
                    <p><span className="library-rag-sourced-timeline-excerpt-label">Supporting excerpt:</span> “{displayExcerpt}”</p>
                ) : null}
                <div className="library-rag-sourced-timeline-footer">
                    <div className="library-rag-sourced-timeline-sources">
                        {(sourcesExpanded ? sources : sources.slice(0, sourcePreviewLimit)).map((source, sourceIndex) => {
                            const citation = getSourcedTimelineCitation(source, citations);
                            const citationNumber = Number(source?.citation_number || citation?.number || 0);
                            const sourceDocument = String(citation?.file_name || source?.file_name || source?.label || `Source ${sourceIndex + 1}`).trim();
                            const physicalPage = Number(citation?.page_start || citation?.page || source?.page_start || source?.page || 0);
                            const label = `${citationNumber > 0 ? `[${citationNumber}] ` : ''}${sourceDocument}${physicalPage > 0 ? ` · physical page ${physicalPage}` : ''}`;
                            return citation ? (
                                <button
                                    type="button"
                                    key={`timeline-source-${sourceIndex}`}
                                    className="library-rag-sourced-timeline-source"
                                    onClick={() => onCitationOpen?.(citation)}
                                    title={String(citation?.label || citation?.file_name || label)}
                                >
                                    {label}
                                </button>
                            ) : (
                                <span key={`timeline-source-${sourceIndex}`} className="library-rag-sourced-timeline-source inert">{label}</span>
                            );
                        })}
                        {hiddenSourceCount > 0 ? (
                            <button
                                type="button"
                                className="library-rag-sourced-timeline-expand"
                                aria-expanded={sourcesExpanded}
                                onClick={() => setSourcesExpanded(prev => !prev)}
                            >
                                {sourcesExpanded ? 'Show fewer sources' : `View ${hiddenSourceCount} more sources`}
                            </button>
                        ) : null}
                    </div>
                    {expandText && !full && (supportingExcerpt.length > 260 || clusteredEvents.length > 1) ? (
                        <button type="button" className="library-rag-sourced-timeline-expand" onClick={() => setExpanded(prev => !prev)}>
                            {expanded ? 'Collapse' : 'Expand'}
                        </button>
                    ) : null}
                </div>
                {showCompleteText && clusteredEvents.length > (expandText ? 1 : 0) ? (
                    <div className="library-rag-sourced-timeline-cluster">
                        {visibleClusteredEvents.map((clusteredEvent, clusteredIndex) => (
                            <div key={`clustered-event-${clusteredIndex}`}>
                                <span>{String(clusteredEvent?.date || event?.date || 'unknown')}</span>
                                <p>{String(clusteredEvent?.summary || clusteredEvent?.title || '').trim()}</p>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </article>
    );
};

const SourcedTimelineReview = ({ timeline, citations = [], onCitationOpen, messageId = 'sourced-timeline', full = false, expandText = true }) => {
    const [selectedType, setSelectedType] = useState('all');
    const [importance, setImportance] = useState('all');
    const [expanded, setExpanded] = useState(false);
    const events = Array.isArray(timeline?.events) ? timeline.events : [];
    const filterOptions = getSourcedTimelineFilterOptions(events);
    const filteredEvents = events.filter((event) => {
        const eventImportance = String(event?.importance || 'low');
        if (!eventMatchesTimelineFilter(event, selectedType)) return false;
        if (importance === 'high' && eventImportance !== 'high') return false;
        if (importance === 'high-medium' && !['high', 'medium'].includes(eventImportance)) return false;
        return true;
    });
    const visibleEvents = !expandText || full || expanded ? filteredEvents : filteredEvents.slice(0, 8);
    const CoverageContainer = expandText ? 'details' : 'section';
    const CoverageHeading = expandText ? 'summary' : 'h4';
    const coverageContainerProps = expandText ? { open: full } : {};
    const groupedCount = Number(timeline?.stats?.grouped_events || 0);
    const coverage = timeline?.coverage && typeof timeline.coverage === 'object' ? timeline.coverage : {};
    const warnings = Array.isArray(timeline?.warnings) ? timeline.warnings : [];
    const artifactGapDocuments = Array.isArray(coverage?.artifact_gap_documents) ? coverage.artifact_gap_documents : [];
    const coverageCounts = [
        ['Matched', Number(coverage?.matched_document_count || 0)],
        ['Artifact-ready', Number(coverage?.artifact_ready_document_count || 0)],
        ['Citation-ready', Number(coverage?.citation_ready_document_count || 0)],
        ['Event-bearing', Number(coverage?.event_bearing_document_count || 0)],
    ];
    const hasDocumentCoverage = timeline?.requestedDocumentTypes?.length > 0
        || Object.prototype.hasOwnProperty.call(coverage, 'matched_document_count');

    return (
        <section className={`library-rag-sourced-timeline ${full ? 'full' : 'inline'}`} aria-label="Sourced timeline review" tabIndex={full ? 0 : undefined}>
            <div className="library-rag-sourced-timeline-hero">
                <div>
                    <span className="library-rag-sourced-timeline-kicker">Sourced timeline</span>
                    <h3>{timeline?.caseName || 'Case timeline'}</h3>
                    <p>{events.length} surfaced event{events.length === 1 ? '' : 's'}{groupedCount > 0 ? ` · ${groupedCount} repetitive updates grouped` : ''}</p>
                </div>
            </div>
            {hasDocumentCoverage ? (
                <div className="library-rag-sourced-timeline-coverage" aria-label="Document chronology coverage counts">
                    {coverageCounts.map(([label, value]) => (
                        <div key={label}>
                            <strong>{value}</strong>
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
            ) : null}
            {warnings.length > 0 ? (
                <CoverageContainer className="library-rag-sourced-timeline-gaps" {...coverageContainerProps}>
                    <CoverageHeading>Coverage and limit gaps ({warnings.length})</CoverageHeading>
                    <ul>
                        {warnings.map((warning, warningIndex) => (
                            <li key={`${warning?.code || 'timeline-gap'}-${warningIndex}`}>
                                <strong>{titleCaseValue(warning?.code || 'Timeline gap')}:</strong> {String(warning?.message || warning?.code || 'Timeline coverage gap')}
                            </li>
                        ))}
                    </ul>
                </CoverageContainer>
            ) : null}
            {artifactGapDocuments.length > 0 ? (
                <CoverageContainer className="library-rag-sourced-timeline-gaps" {...coverageContainerProps}>
                    <CoverageHeading>Document-specific gaps ({Number(coverage?.artifact_gap_document_count || artifactGapDocuments.length)})</CoverageHeading>
                    <ul>
                        {artifactGapDocuments.map((document, documentIndex) => {
                            const name = String(document?.file_name || `File ${document?.file_id || documentIndex + 1}`);
                            const types = Array.isArray(document?.gap_types) ? document.gap_types.map(titleCaseValue).join(', ') : '';
                            const pages = Array.isArray(document?.ocr_uncitable_pages) ? document.ocr_uncitable_pages.join(', ') : '';
                            return (
                                <li key={`${document?.file_id || 'file'}-${document?.registry_id || 'registry'}-${documentIndex}`}>
                                    <strong>{name}:</strong> {titleCaseValue(document?.status || 'Unavailable')}
                                    {types ? ` · ${types}` : ''}
                                    {pages ? ` · uncitable physical page(s) ${pages}` : ''}
                                    {Number(document?.low_confidence_block_count || 0) > 0 ? ` · ${Number(document.low_confidence_block_count)} low-confidence OCR line(s)` : ''}
                                </li>
                            );
                        })}
                        {Number(coverage?.artifact_gap_document_omitted_count || 0) > 0 ? (
                            <li>{Number(coverage.artifact_gap_document_omitted_count)} additional document gap identity record(s) omitted by the bounded detail limit; aggregate counts remain exact.</li>
                        ) : null}
                    </ul>
                </CoverageContainer>
            ) : null}
            {events.length === 0 ? <p className="library-rag-sourced-timeline-empty">No valid dated, page-cited events were found in the available evidence.</p> : null}
            <div className="library-rag-sourced-timeline-controls">
                <button type="button" className={importance === 'high-medium' ? 'active' : ''} onClick={() => setImportance('high-medium')}>Key events</button>
                <button type="button" className={importance === 'all' ? 'active' : ''} onClick={() => setImportance('all')}>All</button>
                <button type="button" className={importance === 'high' ? 'active' : ''} onClick={() => setImportance('high')}>High only</button>
                <select value={selectedType} onChange={(event) => setSelectedType(event.target.value)} aria-label="Timeline source type">
                    <option value="all">All sources</option>
                    {filterOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
            </div>
            <p className="library-rag-sourced-timeline-count" role="status">
                Showing {visibleEvents.length} of {filteredEvents.length} matching events · {events.length} total
            </p>
            <div className="library-rag-sourced-timeline-list">
                {visibleEvents.map((event, eventIndex) => (
                    <SourcedTimelineEventCard
                        key={`sourced-timeline-event-${messageId}-${eventIndex}`}
                        event={event}
                        citations={citations}
                        onCitationOpen={onCitationOpen}
                        full={full}
                        expandText={expandText}
                    />
                ))}
            </div>
            {expandText && !full && filteredEvents.length > visibleEvents.length ? (
                <button
                    type="button"
                    className="library-rag-chronology-toggle"
                    onClick={() => setExpanded(true)}
                >
                    Show {filteredEvents.length - visibleEvents.length} more event{filteredEvents.length - visibleEvents.length === 1 ? '' : 's'} here
                </button>
            ) : null}
            {expandText && !full && expanded && filteredEvents.length > 8 ? (
                <button type="button" className="library-rag-chronology-toggle" onClick={() => setExpanded(false)}>Show fewer events</button>
            ) : null}
        </section>
    );
};

export default SourcedTimelineReview;
